import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, LayoutGrid, Map } from "lucide-react";
import SearchBar from "../components/SearchBar";
import PropertyCard from "../components/PropertyCard";
import PropertiesMap from "../components/PropertiesMap";
import api from "../api";

export default function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    ["city", "area", "listing_type", "property_type", "max_price", "q"].forEach((k) => {
      const v = searchParams.get(k);
      if (v) params.set(k, v);
    });
    try {
      const { data } = await api.get(`/properties?${params.toString()}`);
      setProperties(data.properties);
    } catch (e) {
      setProperties([]);
    }
    setLoading(false);
  }, [searchParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onSearch = ({ city, area, listing_type }) => {
    const params = new URLSearchParams(searchParams);
    city ? params.set("city", city) : params.delete("city");
    area ? params.set("area", area) : params.delete("area");
    listing_type ? params.set("listing_type", listing_type) : params.delete("listing_type");
    setSearchParams(params);
  };

  const applyFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    value ? params.set(key, value) : params.delete(key);
    setSearchParams(params);
  };

  const city = searchParams.get("city") || "Pune";
  const listingType = searchParams.get("listing_type");

  return (
    <main className="bg-ink min-h-screen pt-32 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        <h1 className="font-display text-4xl md:text-5xl font-bold text-navy mb-2" data-testid="properties-heading">
          {listingType ? `Properties to ${listingType}` : "Search Properties"}
          <span className="text-brand-500"> in {city}</span>
        </h1>
        <p className="text-neutral-500 mb-8">Refine your search to find the perfect match.</p>

        <SearchBar
          variant="results"
          initial={{ city, area: searchParams.get("area") || "", listing_type: listingType || "" }}
          onSearch={onSearch}
        />

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <span className="flex items-center gap-2 text-neutral-500 text-sm">
            <SlidersHorizontal className="w-4 h-4" /> Filters:
          </span>
          {["", "Flat", "House", "Commercial"].map((t) => (
            <button key={t || "all"} onClick={() => applyFilter("property_type", t)} data-testid={`filter-type-${t || "all"}`}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                (searchParams.get("property_type") || "") === t
                  ? "bg-brand-500 text-white border-brand-500"
                  : "border-neutral-300 text-neutral-600 hover:border-neutral-500"
              }`}>
              {t || "All types"}
            </button>
          ))}
          <select value={searchParams.get("max_price") || ""} onChange={(e) => applyFilter("max_price", e.target.value)} data-testid="filter-max-price"
            className="bg-white border border-neutral-300 rounded-full px-4 py-1.5 text-sm text-neutral-600 focus:outline-none focus:border-brand-500">
            <option value="">Any price</option>
            <option value="30000">Under ₹30k</option>
            <option value="50000">Under ₹50k</option>
            <option value="5000000">Under ₹50 L</option>
            <option value="15000000">Under ₹1.5 Cr</option>
          </select>
          <div className="ml-auto flex items-center gap-1 bg-white border border-neutral-300 rounded-full p-1">
            <button onClick={() => setView("grid")} data-testid="view-grid-btn"
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${view === "grid" ? "bg-brand-500 text-white" : "text-neutral-600"}`}>
              <LayoutGrid className="w-4 h-4" /> List
            </button>
            <button onClick={() => setView("map")} data-testid="view-map-btn"
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${view === "map" ? "bg-brand-500 text-white" : "text-neutral-600"}`}>
              <Map className="w-4 h-4" /> Map
            </button>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <p className="text-neutral-500 py-20 text-center">Loading properties…</p>
          ) : properties.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-neutral-300 rounded-2xl" data-testid="no-results">
              <p className="text-neutral-700 text-lg">No properties match your search.</p>
              <p className="text-neutral-400 mt-2">Try widening your filters or a different area.</p>
            </div>
          ) : (
            <>
              <p className="text-neutral-500 mb-6" data-testid="results-count">
                {properties.length} propert{properties.length === 1 ? "y" : "ies"} found
              </p>
              {view === "map" ? (
                <PropertiesMap properties={properties} />
              ) : (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {properties.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}
